import React from 'react';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import {
  ENTERPRISE_LINK,
  getPlanLimit,
  getPrice,
  getPriceWithDiscount,
  isDisabledPlan,
  planFeaturesV1,
} from '../helpers/PricingHelper';
import { AccountType } from '../../../shared/types/account/AccountType';
import CustomTooltip from '../../../shared/components/common/tooltip/CustomTooltip';
import { AccountResponse } from '../../../shared/models/account/AccountResponse';
import { DiscountResponseData } from '../../../shared/models/pricing/DiscountResponse';
import { ProductResponse } from '../../../shared/models/pricing/ProductResponse';

interface PricingBoxV1Props {
  accountResponse: AccountResponse;
  discount: DiscountResponseData;
  products: ProductResponse;
  openCheckout: (priceId: string, accountType: AccountType) => void;
}

const PricingBoxV1: React.FC<PricingBoxV1Props> = ({
  accountResponse,
  discount,
  products,
  openCheckout,
}) => {
  const { t } = useTranslation();

  return (
    <div className="pricing-page__content">
      {planFeaturesV1().map((plan, index) => {
        const planSlug = plan.slug;
        const isEnterprisePlan = planSlug === 'enterprise';

        const planName = plan.name;

        const priceData = getPrice(planSlug, products);
        const accountType = planSlug.toUpperCase() as AccountType;
        const planLimit = getPlanLimit(planSlug);
        const isDisabled =
          (accountType !== 'TRIAL' &&
            accountResponse.accountType !== 'TRIAL' &&
            (accountResponse.createdMonitorCount > planLimit.maxMonitors ||
              accountResponse.usersOnAccount > planLimit.maxUsers)) ||
          accountResponse.role === 'USER' ||
          (accountResponse.hasSubscription &&
            isDisabledPlan(accountResponse.accountType, accountType, 'v1'));

        return (
          <div
            className={`price-box ${isEnterprisePlan ? 'price-box--promo' : ''}`}
            key={`product-box-${index}`}
          >
            <div className="price-box__content">
              <label className="price-box__title">{planName}</label>
              <div className="price-box__desc">{plan.description}</div>
              <div className="price-box__period-price">
                <span className="price-box__price">
                  {isEnterprisePlan ? (
                    priceData.price
                  ) : (
                    <>
                      <>{getPriceWithDiscount(products?.data, planSlug, discount)}</>
                      {discount && discount.status === 'active' && <s>{priceData.price}</s>}
                    </>
                  )}
                </span>
                <span className="price-box__period">{`${
                  priceData.priceId ? t('perMonth') : t('billedAnnually')
                }`}</span>
              </div>
              <div className="price-box__keys">{t('keyFeatures')}:</div>
              <ul>
                {plan.features.map((feature, featureIndex) => (
                  <li key={`feature-${planSlug}-${featureIndex}`}>{feature.name}</li>
                ))}
              </ul>
            </div>

            {isDisabled ? (
              <>
                <Button
                  className={`btn btn--disabled-tooltip-color`}
                  label={`${t('getStartedWith')} ${planName}`}
                  id={`pricing-${planSlug}`}
                />
                <CustomTooltip
                  tooltipText={accountResponse.role === 'USER' ? 'noPermission' : 'disableSelectPlan'}
                  targetRef={`pricing-${planSlug}`}
                />
              </>
            ) : (
              <Button
                className={`btn ${priceData.priceId ? 'btn--black' : 'btn--blue'}`}
                label={`${t('getStartedWith')} ${planName}`}
                onClick={() =>
                  priceData.priceId
                    ? openCheckout(priceData.priceId, accountType)
                    : window.open(ENTERPRISE_LINK, '_blank', 'noopener,noreferrer')
                }
                disabled={isDisabled}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

export default PricingBoxV1;
